import React, { useState, useEffect } from "react";
import { apiService } from "../services/api";
import { ProjectAccessRequest } from "../types/api";

export const MyAccessRequests: React.FC = () => {
  const [requests, setRequests] = useState<ProjectAccessRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getMyAccessRequests();
      setRequests(response.requests);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load access requests");
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case "pending_email_verification":
        return {
          badge: (
            <span className="px-3 py-1 text-sm font-medium rounded-full bg-yellow-100 text-yellow-800">
              Awaiting Email Verification
            </span>
          ),
          message: "Please verify your email address to complete your access request.",
          color: "yellow",
        };
      case "pending":
        return {
          badge: (
            <span className="px-3 py-1 text-sm font-medium rounded-full bg-blue-100 text-blue-800">
              Under Review
            </span>
          ),
          message: "Your request is under review by the Principal Investigator.",
          color: "blue",
        };
      case "approved":
        return {
          badge: (
            <span className="px-3 py-1 text-sm font-medium rounded-full bg-green-100 text-green-800">
              Approved
            </span>
          ),
          message: "Your access request has been approved!",
          color: "green",
        };
      case "rejected":
        return {
          badge: (
            <span className="px-3 py-1 text-sm font-medium rounded-full bg-red-100 text-red-800">
              Rejected
            </span>
          ),
          message: "Your access request was not approved.",
          color: "red",
        };
      default:
        return {
          badge: (
            <span className="px-3 py-1 text-sm font-medium rounded-full bg-gray-100 text-gray-800">
              {status}
            </span>
          ),
          message: "",
          color: "gray",
        };
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800">My Access Requests</h1>
        <p className="text-gray-600 mt-2">
          View the status of your project access requests
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {requests.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500 mb-4">No access requests found</p>
          <p className="text-sm text-gray-400">
            Register or request access to a project to get started
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((request) => {
            const statusInfo = getStatusInfo(request.status);

            return (
              <div
                key={request.id}
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-lg font-semibold text-gray-800">
                        Access Request #{request.id}
                      </h3>
                      {statusInfo.badge}
                    </div>

                    {statusInfo.message && (
                      <div
                        className={`p-3 rounded-lg mb-3 bg-${statusInfo.color}-50 border border-${statusInfo.color}-200`}
                      >
                        <p className={`text-sm text-${statusInfo.color}-800`}>
                          {statusInfo.message}
                        </p>
                      </div>
                    )}

                    {request.project_title_description && (
                      <p className="text-sm text-gray-600 mb-2">
                        <span className="font-medium">Project Interest:</span>{" "}
                        {request.project_title_description}
                      </p>
                    )}

                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Submitted:</span>{" "}
                      {new Date(request.created_at).toLocaleDateString()}
                    </p>

                    {request.status === "approved" && request.approved_project_id && (
                      <div className="mt-3 p-3 bg-green-50 rounded-lg">
                        <p className="text-sm font-medium text-green-800 mb-1">
                          Approved for Project
                        </p>
                        <p className="text-sm text-green-700">
                          <span className="font-medium">Role:</span>{" "}
                          {request.assigned_role}
                        </p>
                        {request.reviewed_at && (
                          <p className="text-xs text-green-600 mt-1">
                            Approved on{" "}
                            {new Date(request.reviewed_at).toLocaleDateString()}
                          </p>
                        )}
                        {request.review_notes && (
                          <p className="text-xs text-green-700 mt-2">
                            <span className="font-medium">Notes:</span>{" "}
                            {request.review_notes}
                          </p>
                        )}
                      </div>
                    )}

                    {request.status === "rejected" && (
                      <div className="mt-3 p-3 bg-red-50 rounded-lg">
                        <p className="text-sm font-medium text-red-800 mb-1">
                          Request Not Approved
                        </p>
                        {request.reviewed_at && (
                          <p className="text-xs text-red-600">
                            Reviewed on{" "}
                            {new Date(request.reviewed_at).toLocaleDateString()}
                          </p>
                        )}
                        {request.review_notes && (
                          <p className="text-sm text-red-700 mt-2">
                            <span className="font-medium">Reason:</span>{" "}
                            {request.review_notes}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
